import { SelectItemModel } from '@cp/ds/src/components/select/model';
import { addMilliseconds, differenceInMilliseconds, isToday, parse, startOfDay } from 'date-fns';
import React from 'react';
import { MS_IN_DAY } from '@common/const/time';
import { CATALOG_ITEM_SCHEDULE_INTERVAL_TIME_STEP_IN_MS } from '@common/const/catalog/item';
import { DEAL_LENGTH_MIN_IN_MS, DEAL_START_MIN_OFFSET_FROM_NOW_IN_MS } from '@common/const/deal';
import { msToTimeString } from '@cp/shared/lib';
import { DATE_PICKER_VALUE_DATE_FORMAT_DEFAULT } from '@cp/ds/src/components/form/components/field/dateTimePicker/const';

export const TIME_STEP_IN_MS = CATALOG_ITEM_SCHEDULE_INTERVAL_TIME_STEP_IN_MS;

interface IUseTimeSelectItemsParameters {
  date: string | undefined | null;
  startedAt: string | undefined | null;
  onResetDateTimeFields: () => void;
}

interface IUseTimeSelectItemsReturn {
  startedAtItems: SelectItemModel[];
  endedAtItems: SelectItemModel[];
}

export const useTimeSelectItems = (parameters: IUseTimeSelectItemsParameters): IUseTimeSelectItemsReturn => {
  const { date: dateStr, startedAt: startedAtStr, onResetDateTimeFields } = parameters;

  return React.useMemo(() => {
    const result: IUseTimeSelectItemsReturn = { startedAtItems: [], endedAtItems: [] };

    if (!dateStr) {
      return result;
    }

    const date = parse(dateStr, DATE_PICKER_VALUE_DATE_FORMAT_DEFAULT, new Date());
    const dateStartOfDay = startOfDay(date);

    if (date < startOfDay(new Date())) {
      onResetDateTimeFields();
      return result;
    }

    let availableRangeFromInMs = 0;
    let availableRangeToInMs = MS_IN_DAY - DEAL_LENGTH_MIN_IN_MS;

    if (isToday(date)) {
      const msFromDayStart = differenceInMilliseconds(new Date(), dateStartOfDay) + DEAL_START_MIN_OFFSET_FROM_NOW_IN_MS;
      const timeStepsFromDayStart = Math.ceil(msFromDayStart / TIME_STEP_IN_MS);

      availableRangeFromInMs = timeStepsFromDayStart * TIME_STEP_IN_MS;
    }

    let valueMs = availableRangeFromInMs;

    while (valueMs <= availableRangeToInMs) {
      const valueDate = addMilliseconds(dateStartOfDay, valueMs);
      result.startedAtItems.push({ value: valueDate.toISOString(), label: msToTimeString(valueMs) });
      valueMs += TIME_STEP_IN_MS;
    }

    if (!startedAtStr) {
      return result;
    }

    const startedAt = new Date(startedAtStr);

    availableRangeFromInMs = differenceInMilliseconds(startedAt, dateStartOfDay) + DEAL_LENGTH_MIN_IN_MS;
    availableRangeToInMs = MS_IN_DAY;

    valueMs = availableRangeFromInMs;

    while (valueMs <= availableRangeToInMs) {
      const valueDate = addMilliseconds(dateStartOfDay, valueMs);
      result.endedAtItems.push({ value: valueDate.toISOString(), label: msToTimeString(valueMs) });
      valueMs += TIME_STEP_IN_MS;
    }

    return result;
  }, [dateStr, onResetDateTimeFields, startedAtStr]);
};
